"use strict";
import { useRecentConnectorId } from "components/Web3Provider/constants";
import { useConnect } from "hooks/useConnect";
import { useCallback, useMemo } from "react";
import { CONNECTION_PROVIDER_IDS } from "uniswap/src/constants/web3";
import { isPlaywrightEnv } from "utilities/src/environment/env";
import { isMobileWeb } from "utilities/src/platform";
const SHOULD_THROW = { shouldThrow: true };
function getConnectorWithId(connectors, id, options) {
  const connector = connectors.find((c) => c.id === id);
  if (!connector && options?.shouldThrow) {
    throw new Error(`Expected connector ${id} missing from wagmi context.`);
  }
  return connector;
}
export function useConnectorWithId(id, options) {
  const { connectors } = useConnect();
  return useMemo(
    () => options?.shouldThrow ? getConnectorWithId(connectors, id, options) : getConnectorWithId(connectors, id),
    [connectors, id, options]
  );
}
function getInjectedConnectors(connectors) {
  let isCoinbaseWalletBrowser = false;
  const injectedConnectors = connectors.filter((c) => {
    if (c.id === CONNECTION_PROVIDER_IDS.COINBASE_RDNS) {
      if (isMobileWeb) {
        isCoinbaseWalletBrowser = true;
      }
      return false;
    }
    if (c.id === CONNECTION_PROVIDER_IDS.UNISWAP_EXTENSION_RDNS) {
      return false;
    }
    return c.type === CONNECTION_PROVIDER_IDS.INJECTED_CONNECTOR_TYPE && c.id !== CONNECTION_PROVIDER_IDS.INJECTED_CONNECTOR_ID;
  });
  const fallbackInjector = getConnectorWithId(connectors, CONNECTION_PROVIDER_IDS.INJECTED_CONNECTOR_ID, {
    shouldThrow: true
  });
  if (!injectedConnectors.length && Boolean(window.ethereum)) {
    return { injectedConnectors: [fallbackInjector], isCoinbaseWalletBrowser };
  }
  return { injectedConnectors, isCoinbaseWalletBrowser };
}
export function useOrderedConnections() {
  const { connectors } = useConnect();
  const recentConnectorId = useRecentConnectorId();
  const sortByRecent = useCallback(
    (a, b) => {
      if (a.id === recentConnectorId) {
        return -1;
      } else if (b.id === recentConnectorId) {
        return 1;
      } else {
        return 0;
      }
    },
    [recentConnectorId]
  );
  return useMemo(() => {
    const { injectedConnectors: injectedConnectorsBase, isCoinbaseWalletBrowser } = getInjectedConnectors(connectors);
    const injectedConnectors = injectedConnectorsBase.map((c) => ({ ...c, isInjected: true }));
    const coinbaseSdkConnector = getConnectorWithId(
      connectors,
      CONNECTION_PROVIDER_IDS.COINBASE_SDK_CONNECTOR_ID,
      SHOULD_THROW
    );
    const walletConnectConnector = getConnectorWithId(
      connectors,
      CONNECTION_PROVIDER_IDS.WALLET_CONNECT_CONNECTOR_ID,
      SHOULD_THROW
    );
    if (!coinbaseSdkConnector || !walletConnectConnector) {
      throw new Error("Expected connector(s) missing from wagmi context.");
    }
    if (isPlaywrightEnv()) {
      const mockConnector = getConnectorWithId(connectors, CONNECTION_PROVIDER_IDS.MOCK_CONNECTOR_ID, SHOULD_THROW);
      return [mockConnector];
    }
    if (isMobileWeb && injectedConnectors.length === 1) {
      return injectedConnectors;
    }
    if (isCoinbaseWalletBrowser) {
      return [coinbaseSdkConnector];
    }
    const orderedConnectors = [];
    orderedConnectors.push(...injectedConnectors);
    orderedConnectors.push(walletConnectConnector);
    orderedConnectors.push(coinbaseSdkConnector);
    orderedConnectors.sort(sortByRecent);
    return orderedConnectors;
  }, [connectors, sortByRecent]);
}
export var ExtensionRequestMethods = /* @__PURE__ */ ((ExtensionRequestMethods2) => {
  ExtensionRequestMethods2["OPEN_SIDEBAR"] = "uniswap_openSidebar";
  return ExtensionRequestMethods2;
})(ExtensionRequestMethods || {});
const ExtensionRequestArguments = {
  ["uniswap_openSidebar" /* OPEN_SIDEBAR */]: ["Tokens", "Activity"]
};
export function useUniswapExtensionConnector() {
  const connector = useConnectorWithId(CONNECTION_PROVIDER_IDS.UNISWAP_EXTENSION_RDNS);
  const extensionRequest = useCallback(
    async (method, arg) => {
      const provider = await connector?.getProvider();
      if (!provider.request) {
        return;
      }
      await provider.request({
        method,
        params: [arg]
      });
    },
    [connector]
  );
  return useMemo(() => {
    return connector ? { ...connector, extensionRequest } : void 0;
  }, [connector, extensionRequest]);
}
